import { dateTimeFormatter } from '../../../utils/dateTimeParts';
import type {
  RosterUploadOrganization,
  RosterUploadSourceType,
  RosterUploadStatusFilter,
} from '../../../types/adminRosterUploads';

/**
 * Display rules for Uploaded Rosters. Pure: `now` is an argument wherever the answer depends on it,
 * so these are testable without freezing the clock.
 */

export const EMPTY_CELL = '—';

const integerFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

export const formatCount = (value: number | null | undefined): string =>
  value === null || value === undefined ? EMPTY_CELL : integerFormat.format(value);

/** An instant in the viewer's zone, e.g. "Sep 24, 2026, 1:05 PM". */
export const formatInstant = (iso: string | null | undefined): string => {
  if (!iso) return EMPTY_CELL;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return EMPTY_CELL;
  return dateTimeFormatter('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(at);
};

/** "Just now", "5m ago", "3h ago", "2d ago", then a plain date past a week. */
export const formatRelative = (iso: string | null | undefined, now: Date): string => {
  if (!iso) return EMPTY_CELL;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return EMPTY_CELL;

  const diffMs = now.getTime() - at.getTime();
  // A clock a little ahead of ours should not read as "in the future".
  if (diffMs < 60_000) return 'Just now';
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  return dateTimeFormatter('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(at);
};

/** "850 ms", "12.3 s", "2m 05s". */
export const formatDuration = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return EMPTY_CELL;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  return `${minutes}m ${String(whole % 60).padStart(2, '0')}s`;
};

export const STATUS_FILTER_LABEL: Record<RosterUploadStatusFilter, string> = {
  in_progress: 'In progress',
  pending: 'Pending',
  assessing: 'Assessing',
  assessed: 'Assessed',
  extracting: 'Extracting',
  completed: 'Completed',
  failed: 'Failed',
};

export const SOURCE_FILTER_LABEL: Record<RosterUploadSourceType, string> = {
  image: 'Image',
  csv: 'CSV',
  excel_converted: 'Excel',
};

export type StatusTone = 'success' | 'danger' | 'progress' | 'neutral';

const IN_PROGRESS = new Set(['pending', 'assessing', 'assessed', 'extracting']);

/** A status's label and tone. The word is always rendered, so tone is never the only signal. */
export const statusPresentation = (status: string | null): { label: string; tone: StatusTone } => {
  if (!status) return { label: 'Unknown', tone: 'neutral' };
  if (status === 'completed') return { label: 'Completed', tone: 'success' };
  if (status === 'failed') return { label: 'Failed', tone: 'danger' };
  if (IN_PROGRESS.has(status)) {
    return { label: STATUS_FILTER_LABEL[status as RosterUploadStatusFilter], tone: 'progress' };
  }
  return { label: humanize(status), tone: 'neutral' };
};

/**
 * A label table's own entry for `key`, or undefined.
 *
 * The keys come from tenant data, so a plain index would find inherited members: `"__proto__"`
 * returns `Object.prototype` and `"constructor"` a function, and rendering either throws.
 */
const ownLabel = (table: Readonly<Record<string, string>>, key: string): string | undefined =>
  Object.hasOwn(table, key) ? table[key] : undefined;

/** "Image", "CSV", "Excel", or the raw value made readable for a legacy source. */
export const sourceLabel = (sourceType: string | null): string => {
  if (!sourceType) return 'Unknown';
  return ownLabel(SOURCE_FILTER_LABEL, sourceType) ?? humanize(sourceType);
};

const UPLOAD_TYPE_LABEL: Record<string, string> = {
  single_team_roster: 'Team roster',
  showcase: 'Showcase',
};

export const uploadTypeLabel = (uploadType: string | null): string => {
  if (!uploadType) return EMPTY_CELL;
  return ownLabel(UPLOAD_TYPE_LABEL, uploadType) ?? humanize(uploadType);
};

export const filesLabel = (count: number): string => `${count} ${count === 1 ? 'file' : 'files'}`;

/** `excel_converted` → "Excel converted". */
export const humanize = (value: string): string => {
  const spaced = value.replace(/[_-]+/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : value;
};

/** The first `shown` team names and how many more there are. */
export const summarizeTeams = (
  teamNames: readonly string[],
  shown = 2
): { visible: string[]; remaining: number } => ({
  visible: teamNames.slice(0, shown),
  remaining: Math.max(teamNames.length - shown, 0),
});

export interface OrganizationOption {
  id: string;
  label: string;
}

/**
 * `SelectPopover` speaks in labels, so each organization needs one that is unique. Two
 * organizations can share a name; the second gets a short id suffix rather than silently
 * becoming unselectable.
 */
export const buildOrganizationOptions = (
  organizations: readonly RosterUploadOrganization[]
): OrganizationOption[] => {
  const seen = new Set<string>();
  return organizations.map((org) => {
    const base = `${org.name || 'Unnamed organization'} (${formatCount(org.uploadCount)})`;
    const label = seen.has(base) ? `${base} · ${org.id.slice(0, 8)}` : base;
    seen.add(label);
    return { id: org.id, label };
  });
};
